import React, { FC, MouseEventHandler } from 'react';
import {
  TypeImage,
  TypeMediaTextTitle,
  TypeProduct,
  TypeVideo,
} from '@/core/generated-types';
import { HeaderText, UniversalCta } from '@/components';
import { HeaderSize } from '@/components/ui/HeaderText';
import { usePathname } from '@/navigation';
import { shouldUseYotpoReviews } from '@/utils/helpers';
import { YotpoReviewStars } from '@/components/ui/YotpoReviewStars';
import { CarouselProducts } from '@/components/common/Carousel';
import scrollTo from '@/utils/scrollTo';
import { useSubFlowV3Data } from '@/components/subflow-v3/context/SubFlowV3Context';

interface PlayKitInterface {
  product: TypeProduct;
  carousel: TypeMediaTextTitle[] | TypeVideo[] | TypeImage[];
  onCTAClick: MouseEventHandler<HTMLButtonElement>;
  aboutHeading: string;
  noCommitmentMessage: string;
  featuresHeading: string;
}

interface PlayKitBodyInterface {
  longDescription?: string;
  productValueProps?: TypeMediaTextTitle[];
  featuresHeading: string;
  aboutHeading?: string;
}

export const PlayKitDetailsBody: FC<PlayKitBodyInterface> = ({
  longDescription,
  productValueProps,
  aboutHeading,
  featuresHeading,
}) => {
  return (
    <>
      <HeaderText
        classes="mb-2 md:text-3xl lg:text-3xl"
        size={HeaderSize.H3}
        text={aboutHeading}
      />
      {longDescription && (
        <div
          className="mb-4 leading-6"
          dangerouslySetInnerHTML={{ __html: longDescription }}
          onClick={(e) => {
            const anchor = e.target as HTMLAnchorElement;
            if (anchor.tagName === 'A' && anchor.hash) {
              e.preventDefault(); // Prevent default anchor behavior
              scrollTo({
                id: anchor.hash,
                offSet: -80,
              });
            }
          }}
        />
      )}

      <HeaderText
        classes="mb-2 lg:text-2xl"
        size={HeaderSize.H3}
        text={featuresHeading}
      />
      <ul className="mx-4 flex flex-col gap-2 list-disc">
        {productValueProps?.map((item) => {
          const {
            sys: { id },
            fields: { textHeading },
          } = item;

          return (
            <li key={id} className="list-item">
              <p className="w-auto">{textHeading}</p>
            </li>
          );
        })}
      </ul>
    </>
  );
};

export const PlayKitDetailsHeader: React.FC<PlayKitInterface> = ({
  product,
  carousel,
  onCTAClick,
  aboutHeading,
  noCommitmentMessage,
  featuresHeading,
}) => {
  const {
    sys: { id },
  } = product;
  const { title, featuredImage, shipMessage, cta, ageDescription } =
    product.fields;

  const pathname = usePathname();
  const {
    api: { updateInitialProduct },
  } = useSubFlowV3Data();

  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    updateInitialProduct(product);
    onCTAClick(event);
  };

  return (
    <div className="lg:flex lg:gap-6">
      <div className="pb-3 -m-6 md:mx-0 lg:p-0 lg:m-0 lg:basis-7/12">
        <CarouselProducts
          id={id}
          components={[featuredImage, ...carousel]}
          key={pathname}
        />
      </div>
      <div className="mt-10 lg:mt-0 lg:basis-5/12">
        <HeaderText text={title} size={HeaderSize.H1} />
        <p className="text-base font-bold md:text-lg">{ageDescription}</p>
        {shouldUseYotpoReviews() && (
          <div className="my-3 md:mb-3">
            <YotpoReviewStars product={product} />
          </div>
        )}
        {shipMessage && (
          <h3
            className="font-bold w-full text-charcoal text-h3 md:text-d-h3"
            role="heading"
            dangerouslySetInnerHTML={{ __html: shipMessage }}
          />
        )}
        <div className="col-span-12">
          {cta && (
            <UniversalCta
              link={cta}
              className="mt-5 mx-0 h-14 lg:h-16 lg:max-w-[360px]"
              onClick={handleClick}
            />
          )}
          <p
            className="pb-2 mt-3 text-sm md:text-base"
            dangerouslySetInnerHTML={{ __html: noCommitmentMessage }}
          />

          <div className="border-b-[1px] border-charcoal-opacity-20 mt-1 mb-4 lg:mt-7 lg:mb-6" />
          <PlayKitDetailsBody
            longDescription={product.fields.longDescription}
            productValueProps={
              product.fields.productValueProps as TypeMediaTextTitle[]
            }
            aboutHeading={aboutHeading}
            featuresHeading={featuresHeading}
          />
        </div>
      </div>
    </div>
  );
};
